import {
  ListPage, Badge, AsyncBoundary, useToast,
} from '@kollez/design-system';
import { formatLPA } from '@kollez/shared';
import { prettyOpportunityType } from '@kollez/shared/enums';
import { useNavigate } from 'react-router-dom';
import {
  useOpportunities, usePublishOpportunity, useCloseOpportunity,
  useOpportunityApplicationsSummary,
  type OpportunityRow,
} from '@/lib/queries';

/**
 * Pass 68: applications-summary card inside the opening drawer.
 * GET /opportunities/:id/applications-summary returns total + byStatus +
 * topColleges (top 5) + matchScore { avg, scored }. Read-only, cheap.
 */
function ApplicationsSummary({ opportunityId }: { opportunityId: string }) {
  const { data, isLoading, error } = useOpportunityApplicationsSummary(opportunityId);
  if (isLoading) return <div className="cell-sub">Loading applicants…</div>;
  if (error) return <div className="cell-sub">Couldn't load applicants summary.</div>;
  if (!data || data.total === 0) return null;
  const { total, byStatus, topColleges, matchScore } = data;
  const stageOrder: Array<[string, string]> = [
    ['Applied', 'Applied'],
    ['Under_Review', 'Under review'],
    ['Shortlisted', 'Shortlisted'],
    ['Interview_Scheduled', 'Interview'],
    ['Offered', 'Offered'],
    ['Accepted', 'Accepted'],
    ['Rejected', 'Rejected'],
    ['Withdrawn', 'Withdrawn'],
  ];
  const shown = stageOrder.filter(([k]) => (byStatus[k] ?? 0) > 0);
  return (
    <div className="grid gap-2 pt-2 border-t border-line-tertiary">
      <div className="text-ink-tertiary text-xs">Applicants ({total})</div>
      <div className="flex flex-wrap gap-1">
        {shown.map(([k, label]) => (
          <span key={k} className="chip text-xs">
            {label} · <b>{byStatus[k]}</b>
          </span>
        ))}
      </div>
      {matchScore.avg !== null && matchScore.scored > 0 && (
        <div className="cell-sub">
          Median-ish match: <b>{Math.round(matchScore.avg)}</b> ({matchScore.scored} scored)
        </div>
      )}
      {topColleges.length > 0 && (
        <div className="cell-sub">
          Top colleges: {topColleges.map((c, i) => (
            <span key={String(c.college ?? i)}>
              {i > 0 ? ' · ' : ''}
              {c.college ? String(c.college).slice(-6) : 'unknown'} ({c.count})
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_TONE: Record<OpportunityRow['status'], 'success' | 'info' | 'warning' | 'outline'> = {
  Active: 'success',
  Draft: 'outline',
  Paused: 'warning',
  Closed: 'outline',
  Archived: 'outline',
};

export function CompanyOpeningsPage() {
  const nav = useNavigate();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useOpportunities();
  const publish = usePublishOpportunity();
  const close = useCloseOpportunity();
  const rows = data?.opportunities ?? [];

  async function handlePublish(id: string) {
    try { await publish.mutateAsync(id); toast.show({ title: 'Published', message: 'Now visible to students who match eligibility.' }); }
    catch { toast.show({ tone: 'warning', message: 'Publish failed' }); }
  }
  async function handleClose(id: string) {
    try { await close.mutateAsync(id); toast.show({ message: 'Opening closed.' }); }
    catch { toast.show({ tone: 'warning', message: 'Close failed' }); }
  }

  return (
    <AsyncBoundary
      kind="table"
      loading={isLoading}
      error={error}
      empty={!isLoading && rows.length === 0}
      emptyTitle="No openings yet"
      emptyHint="Post your first opening to start receiving applications."
      emptyAction={<button className="btn btn-primary btn-sm" onClick={() => nav('/company/openings/new')}>+ Post opening</button>}
      onRetry={() => void refetch()}
    >
      <ListPage<OpportunityRow>
        eyebrow="Hiring"
        title="Openings"
        subtitle="Your active + draft openings. Publish once to make eligible students see them."
        actions={[{ label: '+ Post opening', primary: true, onClick: () => nav('/company/openings/new') }]}
        kpis={[
          { label: 'Total openings', value: rows.length },
          { label: 'Active', value: rows.filter((r) => r.status === 'Active').length, tone: 'success' },
          { label: 'Drafts', value: rows.filter((r) => r.status === 'Draft').length, tone: 'info' },
          { label: 'Applications so far', value: rows.reduce((s, r) => s + r.applicationCount, 0), tone: 'info' },
        ]}
        segment={{
          key: 'status',
          options: [['all', 'All'], ['Active', 'Active'], ['Draft', 'Draft'], ['Paused', 'Paused'], ['Closed', 'Closed']],
        }}
        search={{ placeholder: 'Search title', keys: ['title'] }}
        rows={rows}
        rowKey={(r) => r._id}
        columns={[
          {
            key: 'title',
            label: 'Opening',
            render: (r) => (
              <div>
                <div className="cell-title">{r.title}</div>
                <div className="cell-sub">{prettyOpportunityType(r.type) || 'opening'} · scope: {r.scope ?? '—'}</div>
              </div>
            ),
          },
          {
            key: 'ctc',
            label: 'CTC',
            right: true,
            hideMobile: true,
            render: (r) => r.compensation?.ctc?.min ? formatLPA(r.compensation.ctc.min) : '—',
          },
          {
            key: 'apps',
            label: 'Applications',
            right: true,
            render: (r) => r.applicationCount,
          },
          {
            key: 'status',
            label: 'Status',
            right: true,
            render: (r) => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>,
          },
        ]}
        drawer={(r) => ({
          eyebrow: prettyOpportunityType(r.type) || 'opening',
          title: r.title,
          size: 'lg',
          body: (
            <div className="grid gap-3 text-sm">
              {r.description && (
                <p className="whitespace-pre-line text-ink-secondary">{r.description.slice(0, 400)}{r.description.length > 400 && '…'}</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-ink-tertiary">Min CGPA</span><div><b>{r.eligibility?.minCGPA ?? '—'}</b></div></div>
                <div><span className="text-ink-tertiary">Max backlogs</span><div><b>{r.eligibility?.maxBacklogs ?? '—'}</b></div></div>
                <div><span className="text-ink-tertiary">Branches</span><div><b>{r.eligibility?.eligibleBranches?.join(', ') || 'Any'}</b></div></div>
                <div><span className="text-ink-tertiary">Locations</span><div><b>{r.locations?.map((l) => l.city).join(', ') || '—'}</b></div></div>
              </div>
              {r.requiredSkills?.length ? (
                <div>
                  <div className="text-ink-tertiary text-xs mb-1">Required skills</div>
                  <div className="flex flex-wrap gap-1">
                    {r.requiredSkills.map((s) => <span key={s} className="chip text-xs">{s}</span>)}
                  </div>
                </div>
              ) : null}
              <ApplicationsSummary opportunityId={r._id} />
              <div className="flex gap-2 pt-2 border-t border-line-tertiary">
                {r.status === 'Draft' && (
                  <button className="btn btn-primary btn-sm" onClick={() => void handlePublish(r._id)} disabled={publish.isPending}>
                    Publish
                  </button>
                )}
                {r.status === 'Active' && (
                  <button className="btn btn-secondary btn-sm" onClick={() => void handleClose(r._id)} disabled={close.isPending}>
                    Close opening
                  </button>
                )}
                <button className="btn btn-secondary btn-sm" onClick={() => nav(`/company/openings/${r._id}`)}>Edit</button>
              </div>
            </div>
          ),
        })}
      />
    </AsyncBoundary>
  );
}
